import {
  Component,
  ComponentWillLoad,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
} from '@stencil/core';

import { createFocusTrap } from 'focus-trap';

import { Appearance } from '@entities/Appearance';
import { Design } from '@entities/Design';
import { DynamicFormValue } from '@entities/DynamicFormValue';
import { ValidateStatus } from '@entities/FieldPayload';
import { BRTQuestion } from '@entities/Question';
import { QuestionResponse } from '@entities/QuestionResponse';
import { WidgetPosition } from '@entities/WidgetPosition';
import { QuestionType } from '@entities/QuestionType';

@Component({
  tag: 'wog-bug-report-form',
})
export class BugReportForm implements ComponentWillLoad {
  @Prop() questions: BRTQuestion[];
  @Prop() position: WidgetPosition;
  @Prop() design: Design;
  @Prop() themeColor: string;
  @Prop() appearance: Appearance;
  @State() isValid = false;
  @State() registry: Record<string, QuestionResponse> = {};
  @State() requiredQuestions: string[] = [];
  @State() preparedQuestions: BRTQuestion[] = [];
  @State() responses: QuestionResponse[];
  @Event() bugReportFormCancel: EventEmitter;
  @Event() bugReportFormSubmit: EventEmitter;
  @Event() bugReportFormEntry: EventEmitter;

  private bugReportFormContainer: HTMLDivElement;
  private trap = createFocusTrap('', {});

  @Listen('keydown', { target: 'body' })
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.handleCancel();
    }
  }

  componentWillLoad(): Promise<void> | void {
    this.preparedQuestions = this.questions.sort((a, b) => a.index - b.index);
    
    if(!this.preparedQuestions.filter(q => q.questionType === QuestionType.DefaultAcknowledgeQuestion).length){
      this.preparedQuestions.push({
        id: Math.floor((Math.random() * 9999)),
        index: this.questions.length,
        isRequired: true,
        label: "I confirm the details above are accurate and used only to resolve issues.",
        questionId: 'acknowledge-question',
        questionType: QuestionType.DefaultAcknowledgeQuestion,
      })
    } 
    const { registry, requiredQuestions } = this.getRegistry(this.preparedQuestions);
    this.registry = registry;
    this.requiredQuestions = requiredQuestions;
  }

  componentDidLoad() {
    this.trap = createFocusTrap(this.bugReportFormContainer, {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: false,
      initialFocus: this.bugReportFormContainer,
      fallbackFocus: this.bugReportFormContainer,
      onPostActivate: () => {
        this.trapDeactivate();
        document.body.focus();
      },
    });
    this.trapActivate();

    // Snowplow event should fire the moment the user sees the bug report form page.
    this.bugReportFormEntry.emit();
  }

  private trapActivate = () => {
    if (!this.trap.active) {
      this.trap.activate();
    }
  };

  private trapDeactivate = () => {
    if (this.trap.active) {
      this.trap.deactivate();
    }
  };

  private getRegistry(questions: BRTQuestion[]) {
    const registry = {};
    let requiredQuestions: string[] = [];
    for (const question of questions) {
      const { index, questionId, questionType, isRequired, label } = question;
      registry[questionId] = {
        index,
        questionId,
        label,
        questionType,
        isRequired,
        isValid: ValidateStatus.None,
        value: null,
      };
      requiredQuestions = isRequired ? [...requiredQuestions, questionId] : requiredQuestions;
    }
    return {
      registry,
      requiredQuestions,
    };
  }

  private handleCancel = () => {
    this.bugReportFormCancel.emit();
  };

  private handleValidStatusChange = (event: CustomEvent<DynamicFormValue>) => {
    const { status, responses } = event.detail;
    this.responses = responses.filter(r => r.questionType !== QuestionType.DefaultAcknowledgeQuestion);
    this.isValid = status;
  };

  private handleSubmit = (event: Event): void => {
    event.preventDefault();
    if (this.isValid) {
      this.trapDeactivate();
      const values = this.responses.map(({ index, questionId, label, questionType, value }) => ({
        questionNumber: index,
        questionId,
        label,
        questionType,
        response: value,
      }));
      this.bugReportFormSubmit.emit({ values });
    }
  };

  render() {
    const rootClass = {
      'wog--tab-view-container': true,
      'wog--bug-report-form': true,
      'wog--flex': true,
      'wog--flex-col': true,
      'wog--modern-feedback-custom': true,
      'wog--mobile-bug-report-form': true,
      'wog--position-left-side': this.position === WidgetPosition.MiddleLeft,
      'wog--position-right-side': this.position === WidgetPosition.MiddleRight,
      'wog--position-bottom-left': this.position === WidgetPosition.BottomLeft,
      'wog--position-bottom-right': this.position === WidgetPosition.BottomRight,
    };

    const submitButtonCx = {
      'wog--button': true,
      'wog--float-right': true,
      'wog--cursor-pointer': this.isValid,
      'wog--opacity-50': !this.isValid,
      'wog--cursor-not-allowed': !this.isValid,
    };

    const backgroundStyles = {
      backgroundColor: this.design !== Design.SmileyFace ? this.themeColor : '#333333',
    };
    const textStyles = {
      color: this.appearance === Appearance.Light ? 'white' : '#333333',
      fontWeight: 'bold',
    };
    const formStyles = {
      width: 'inherit',
      minWidth: 'auto',
      minHeight: '40px',
    };

    return (
      <div
        class={rootClass}
        ref={el => (this.bugReportFormContainer = el as HTMLDivElement)}
        tabIndex={0}
        role="group"
        aria-roledescription="Bug Report Form popup"
        aria-labelledby="wogBugReportFormHeading">
        <wog-close-button
          isWhite={this.appearance === Appearance.Light}
          onClose={this.handleCancel}
        />
        <div
          class="wog--flex wog--items-center wog--justify-center wog--p-4"
          tabIndex={0}
          aria-label="Report an issue"
          style={backgroundStyles}>
          <h3
            id="wogBugReportFormHeading"
            class="wog--text-foundation wog--base-title wog--text-lg"
            style={textStyles}>
            Report an issue
          </h3>
        </div>
        <form
          novalidate
          aria-label="Add a report issue"
          name="wogSentimentsReportForm"
          class="wog--flex wog--h-full wog--flex-col wog--justify-between"
          style={formStyles} 
          onSubmit={(e) => e.preventDefault()}>
          <div class="wog--overflow-y-auto wog--p-4 wog--px-4 wog--text-left">
            <wog-dynamic-form
              onValidStatusChange={this.handleValidStatusChange}
              questions={this.preparedQuestions}
              registry={this.registry}
              requiredQuestions={this.requiredQuestions}
            />
          </div>

          <div class="wog--px-4 wog--py-2">
            <button
              tabIndex={0}
              type="submit"
              id="wogSubmitForm"
              disabled={!this.isValid}
              style={{ ...backgroundStyles, ...textStyles }}
              class={submitButtonCx}
              onClick={this.handleSubmit}>
              SUBMIT
            </button>
          </div>
        </form>
      </div>
    );
  }
}
